// External Libraries
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Assets
import Filter from '../assets/filter.png';
import DisabledFilter from '../assets/disable-filter.png';

// Styles
import '../styles/PeopleFeed.css';

// Redux/State Management
import { RootState } from '../redux/store';

// API Services
import { getFilterUsers, getUsers } from '../api/UserService';

// Types
import {
  EmailThreadProps,
  FilterUsersResponse,
  UserFilterParams,
  UserProfile,
  UserProfilesResponse,
} from '../types/types';

// Components
import PeopleCard from './common/PeopleCard';
import Loading from './common/Loading';
import Conversation from './Conversation';
import PeopleFilter from './PeopleFilter';
import Button from '../components/common/Button';
import Pagination from './common/Pagination';
import Text from './common/Text';

const PeopleFeed: React.FC = () => {
  const navigate = useNavigate();

  const reduxUserTypes = useSelector(
    (state: RootState) => state.userTypes,
  ).userTypes;
  const reduxDepartments = useSelector(
    (state: RootState) => state.departments,
  ).departments;
  const reduxSchools = useSelector((state: RootState) => state.schools).schools;

  const [users, setUsers] = useState<UserProfile[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [isFilterModel, setIsFilterModel] = useState<boolean | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(10);

  const [isConverstationPopup, setIsConverstationPopup] =
    useState<boolean>(false);
  const [conversationDetails, setConversationDetails] =
    useState<EmailThreadProps | null>(null);

  const [usersFilter, setUsersFilter] = useState<UserFilterParams>({
    sortBy: 'asc',
    department_id: null,
    school_id: null,
    user_type_id: [],
  });
  const [isClearFilter, setIsClearFilter] = useState<boolean | null>(null);
  const [activeFilterNames, setActiveFilterNames] = useState<string[] | null>(
    null,
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const pageNumber = queryParams.get('page');
  const [userTypeId, setUserTypeId] = useState<string | null>(
    new URLSearchParams(location.search).get('user_type_id'),
  );
  const [schoolId, setSchoolId] = useState<string | null>(
    new URLSearchParams(location.search).get('school_id'),
  );
  const [departmentId, setDepartmentId] = useState<string | null>(
    new URLSearchParams(location.search).get('department_id'),
  );
  const [userId, setUserId] = useState<string | null>(
    new URLSearchParams(location.search).get('user_id'),
  );
  const [sortOrder, setSortOrder] = useState<string | null>(
    new URLSearchParams(location.search).get('sort_order'),
  );

  const initialPage = pageNumber ? Number(pageNumber) : 1;
  const [currentPage, setCurrentPage] = useState<number>(Number(initialPage));
  const [isFilteredView, setIsFilteredView] = useState<boolean>(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const page = queryParams.get('page');
    if (page) {
      setCurrentPage(Number(page));
    }

    setUserTypeId(queryParams.get('user_type_id'));
    setSchoolId(queryParams.get('school_id'));
    setDepartmentId(queryParams.get('department_id'));
    setUserId(queryParams.get('user_id'));
    setSortOrder(queryParams.get('sort_order'));

    if (
      !queryParams.get('user_type_id') &&
      !queryParams.get('school_id') &&
      !queryParams.get('department_id') &&
      !queryParams.get('user_id') &&
      !queryParams.get('sort_order')
    ) {
      setIsClearFilter(false);
      setIsFilteredView(false);
      setUsersFilter({
        sortBy: 'asc',
        department_id: null,
        school_id: null,
        user_type_id: [],
      });
    }
  }, [location.search]);

  const updateActiveFilters = (
    prevState: string[] | null,
    filterValue: string | null,
  ): string[] => {
    if (filterValue && !prevState?.includes(filterValue)) {
      return [...(prevState ?? []), filterValue];
    }
    return prevState ?? [];
  };

  useEffect(() => {
    if (!pageNumber) {
      setCurrentPage(initialPage);
    }

    let updatedFilters: string[] = [];

    if (userTypeId !== null) {
      setIsClearFilter(true);
      const supportFilterValue =
        reduxUserTypes.find((userType) => userType.id === Number(userTypeId))
          ?.type || null;
      updatedFilters = updateActiveFilters(updatedFilters, supportFilterValue);
    }

    if (schoolId !== null) {
      setIsClearFilter(true);
      const schoolFilterValue =
        reduxSchools.find((school) => school.id === Number(schoolId))?.name ||
        null;
      updatedFilters = updateActiveFilters(updatedFilters, schoolFilterValue);
    }

    if (departmentId !== null) {
      setIsClearFilter(true);
      const departmentFilterValue =
        reduxDepartments.find(
          (department) => department.id === Number(departmentId),
        )?.name || null;
      updatedFilters = updateActiveFilters(
        updatedFilters,
        departmentFilterValue,
      );
    }

    if (sortOrder !== null) {
      setIsClearFilter(true);
      const followingSoryByValue = sortOrder === 'asc' ? 'A-Z' : 'Z-A';
      updatedFilters = updateActiveFilters(
        updatedFilters,
        followingSoryByValue,
      );
    }

    if (userId !== null) {
      setIsClearFilter(true);
      const fetchFilterUsers = async () => {
        const filterUsersResponse = await getFilterUsers();
        if ('code' in filterUsersResponse) {
          console.error('Error fetching filter users:', filterUsersResponse);
        } else {
          const user = (filterUsersResponse as FilterUsersResponse).users.find(
            (user) => Number(userId) === user.id,
          );
          if (user) setSelectedUserFilter(user.name);
        }
      };
      fetchFilterUsers();
    }
    if (selectedUserFilter) {
      updatedFilters = updateActiveFilters(updatedFilters, selectedUserFilter);
    }
    setActiveFilterNames(updatedFilters);
  }, [
    pageNumber,
    userTypeId,
    schoolId,
    departmentId,
    sortOrder,
    userId,
    selectedUserFilter,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const result = await getUsers(
          currentPage,
          pageSize,
          userId !== null ? Number(userId) : (usersFilter.userId ?? 0),
          userTypeId !== null
            ? [Number(userTypeId)]
            : usersFilter.user_type_id !== undefined
              ? usersFilter.user_type_id
              : [],
          schoolId !== null ? Number(schoolId) : (usersFilter.school_id ?? 0),
          departmentId !== null
            ? Number(departmentId)
            : (usersFilter.department_id ?? 0),
          sortOrder === 'asc' || sortOrder === 'desc'
            ? sortOrder
            : (usersFilter.sortBy ?? 'desc'),
        );

        setLoading(false);

        if ('code' in result) {
          console.error('Error fetching getUsers:', result);
          if (result.code === 'NOT_FOUND') {
            if (currentPage > 1) {
              setCurrentPage(1);
              handlePageChange(1);
            } else {
              setMessage(result.message);
            }
          } else {
            setMessage(result.message);
          }
        } else {
          if ((result as UserProfilesResponse).users.length > 0) {
            setUsers((result as UserProfilesResponse).users);
            setMessage(null);
            setCurrentPage((result as UserProfilesResponse).page);
            setTotalPages((result as UserProfilesResponse).total_pages);
          } else {
            console.log('users object is empty');
            setLoading(false);
            setMessage(null);
          }
        }
      } catch (error) {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [currentPage, usersFilter, isFilteredView]);

  const handleContactClick = (email: string, recipientName: string) => {
    setConversationDetails({
      emailAddress: email,
      recipientName: recipientName,
    });
    setIsConverstationPopup(true);
  };

  const handleBlurClick = () => {
    setIsConverstationPopup(false);
    setConversationDetails(null);
  };

  useEffect(() => {
    if (isFilterModel || isConverstationPopup) {
      document.body.classList.add('locked-scroll');
    } else {
      document.body.classList.remove('locked-scroll');
    }
    return () => {
      document.body.classList.remove('locked-scroll');
    };
  }, [isFilterModel || isConverstationPopup]);

  const convertToQueryString = (params: UserFilterParams): string => {
    const queryParams = new URLSearchParams();

    const appendArrayParam = (
      key: string,
      value: number[] | null | undefined,
    ) => {
      if (value && value.length > 0) {
        queryParams.set(key, value.join(','));
      }
    };

    const appendSingleParam = (
      key: string,
      value: string | number | null | undefined,
    ) => {
      if (value !== null && value !== undefined) {
        queryParams.set(key, value.toString());
      }
    };

    appendArrayParam('user_type_id', params.user_type_id);

    appendSingleParam('school_id', params.school_id);
    appendSingleParam('department_id', params.department_id);
    appendSingleParam('user_id', params.userId);
    appendSingleParam('sort_order', params.sortBy);

    return queryParams.toString();
  };

  const handleFilterUsers = (people: UserFilterParams) => {
    setUsersFilter(people);
    setIsFilterModel(false);
    setCurrentPage(1);
    setIsClearFilter(true);
    setLoading(true);
    setIsFilteredView(true);
    const url = convertToQueryString(people);
    if (url) navigate(`/people?` + url);
  };

  const handleClearFilters = () => {
    setIsFilteredView(false);
    setUsersFilter({
      sortBy: 'asc',
      department_id: null,
      school_id: null,
      user_type_id: [],
      userId: null,
    });
    setCurrentPage(1);
    setActiveFilterNames(null);
    setIsClearFilter(false);
    setLoading(true);

    setUserTypeId(null);
    setSchoolId(null);
    setDepartmentId(null);
    setUserId(null);
    setSortOrder(null);
    setSelectedUserFilter(null);

    navigate(`/people`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    const url =
      `/people?page=${page}` +
      (userTypeId !== null ? `&user_type_id=${userTypeId}` : '') +
      (schoolId !== null ? `&school_id=${schoolId}` : '') +
      (departmentId !== null ? `&department_id=${departmentId}` : '') +
      (sortOrder !== null ? `&sort_order=${sortOrder}` : '') +
      (userId !== null ? `&user_id=${userId}` : '');
    navigate(url);
  };

  return (
    <>
      {loading ? (
        <div className="loading-class">
          <Loading color="var(--black)" />
        </div>
      ) : message ? (
        <div className="loading-class">{message}</div>
      ) : users !== undefined && users.length > 0 ? (
        <>
          <div className="feed-header-container">
            <div className="sub-header-container">
              <Text className="sub-header">People</Text>
              <Button
                type="button"
                className={
                  isClearFilter === true && activeFilterNames !== null
                    ? 'disabled-filter-button'
                    : 'filter-button'
                }
                onClick={() => setIsFilterModel(true)}
                disabled={isClearFilter === true}
              >
                {isClearFilter === true && activeFilterNames !== null ? (
                  <img
                    src={DisabledFilter}
                    alt="DisabledFilter"
                    className="filter-image"
                  />
                ) : (
                  <img src={Filter} alt="Filter" className="filter-image" />
                )}
              </Button>
            </div>
            {isClearFilter && activeFilterNames && (
              <>
                {activeFilterNames && (
                  <div className="active-filters">
                    {activeFilterNames.map((filter, index) => (
                      <Button
                        type="button"
                        key={index}
                        className="role-button selected"
                      >
                        {filter}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      onClick={() => handleClearFilters()}
                      className="clear-filter-button"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          {users !== undefined &&
            users.length > 0 &&
            users.map((user: UserProfile) => (
              <PeopleCard
                user={user}
                key={user.id}
                onContactClick={handleContactClick}
              />
            ))}
          <div className="custom-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="loading-class">No People found on this page.</div>
      )}
      {/* Blur Overlay and Popup */}
      {isConverstationPopup && (
        <>
          <div
            className="filter-container contact-bg-color"
            onClick={handleBlurClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleBlurClick();
              }
            }}
          >
            <div className="bottom-popup">
              {conversationDetails && (
                <Conversation
                  emailAddress={conversationDetails.emailAddress}
                  recipientName={conversationDetails.recipientName}
                />
              )}
            </div>
          </div>
        </>
      )}
      {isFilterModel && (
        <PeopleFilter
          toggleFilter={() => setIsFilterModel(!isFilterModel)}
          handleFilterUsers={handleFilterUsers}
        />
      )}
    </>
  );
};

export default PeopleFeed;
